import React, { ChangeEvent } from 'react'
import { useIntl } from 'react-intl'
import { css } from '@emotion/css'
import { Checkbox } from '@contrast-security-inc/design-system-inputs'
import { isAttributeGroupSet, isAttributeSet } from './utils'
import { parentLabelCss } from '../../common'
import { AttributeFilterValues, AttributeListItemType, FilterState } from '../../../../../types'
import { ExpandableList, ListSearchInput, useListSearch } from '../../../layout'
import { EmptyFilterComponent, FilterProps } from '../../filters'

export type AttributeFilters = Record<string, AttributeFilterValues>

const getFilterStatesFromGroup = (
  list: AttributeListItemType[],
  id: string,
  checkState?: boolean
) => {
  const item = list.find((it) => it.keycode === id)

  return !item?.values?.length
    ? [{ id, flag: checkState }]
    : Array.isArray(item.values) &&
        item.values.reduce<FilterState[]>(
          (acc, { keycode: key, value }) => [...acc, { id, key, value, flag: checkState }],
          []
        )
}

const processAttributeList = (listItems: AttributeListItemType[]): AttributeListItemType[] =>
  listItems.reduce<AttributeListItemType[]>((acc, it) => {
    const values = it.values
      ? Object.keys(it.values).reduce<AttributeListItemType[]>((valAcc, key) => {
          const values =
            // @ts-ignore
            it.values?.[key].map((value) => ({
              keycode: key,
              value,
              label: `${key}: ${value}`
            })) ?? []

          return [...valAcc, ...values]
        }, [])
      : []

    return [...acc, { ...it, values }]
  }, [])

export const FilterAttribute = ({
  filterDef: {
    filterMsgId,
    filterKey,
    useFilterList,
    options: { search = false, showAll = false, maxHeight } = {}
  },
  filters,
  updateFilter
}: { filters: AttributeFilters } & Omit<FilterProps, 'filters' | 'resetFilter'>) => {
  // state/hooks
  const intl = useIntl()
  const { data, isLoading } = useFilterList({ filters })
  const listItems = React.useMemo(
    // @ts-ignore we're sure the filters are attribute filters
    () => processAttributeList(data?.data?.filters || []),
    [data?.data?.filters]
  )
  const {
    searchText,
    onChangeSearchText,
    searchedList: filtersList
  } = useListSearch({ subKey: 'values', listItems })
  const searchPlaceholder = intl.formatMessage(
    { id: 'placeholder_search' },
    // dynamic msg id
    { type: intl.formatMessage({ id: filterMsgId }) }
  )

  // events
  const onFilterChange = React.useCallback(
    (event: ChangeEvent<HTMLInputElement>, checkState?: boolean) => {
      const parentId = event.target.getAttribute('data-parent')
      const key = event.target.getAttribute('data-key') ?? ''
      const value = event.target.getAttribute('data-value') ?? ''
      const filterState = parentId // groups have no parent id
        ? { id: parentId, key, value, flag: checkState } // single selection
        : getFilterStatesFromGroup(listItems, key, checkState) // group selection
      // @ts-ignore
      updateFilter(filterKey, filterState)
    },
    [filterKey, updateFilter, listItems]
  )
  const AttributeListItem = React.useCallback(
    ({ parent, label, keycode, value }: AttributeListItemType) => {
      const isGroup = !parent
      const { keycode: parentKeycode } = parent || {}

      return (
        <Checkbox
          className={isGroup ? parentLabelCss : undefined}
          data-parent={parentKeycode}
          data-key={keycode}
          data-value={value}
          testId={`${filterKey}-${keycode}-${value}`}
          label={label}
          onChange={onFilterChange}
          isChecked={
            isGroup
              ? isAttributeGroupSet(filters[filterKey], keycode, listItems)
              : isAttributeSet(filters[filterKey], parentKeycode!, keycode, value)
          }
        />
      )
    },
    [filters, filterKey, onFilterChange, listItems]
  )

  return (
    <div data-testid="filter-attribute">
      {search && (
        <div
          className={css`
            margin-bottom: 16px;
          `}
        >
          <ListSearchInput
            onChangeSearchText={onChangeSearchText}
            searchText={searchText}
            placeholder={searchPlaceholder}
          />
        </div>
      )}
      <div
        className={css`
          display: flex;
          flex-direction: column;
        `}
      >
        <ExpandableList
          maxHeight={maxHeight}
          subKey="values"
          showAll={showAll}
          isLoading={isLoading}
          listItems={filtersList}
          EmptyListComponent={EmptyFilterComponent}
          // @ts-ignore we're sure of this
          ItemComponent={AttributeListItem}
        />
      </div>
    </div>
  )
}
